import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { FileTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as FormTemplateFileTypeActions from "../../../actions/formTemplateFileTypes";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./FormTemplateFileTypesPage.scss";

interface FormTemplateFileTypesPageProps {
    formFilter: IFormFilter;
    formTemplateFileTypes: Dtos.FormTemplateFileType[];
    loadingFormTemplateFileTypes: boolean;
    loadFormTemplateFileTypesSuccess: boolean;
    loadFormTemplateFileTypesFailure: boolean;
    loadFormTemplateFileTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearFormTemplateFileTypes: FormTemplateFileTypeActions.IFormTemplateFileTypeClearActionCreator;
    loadFormTemplateFileTypes: FormTemplateFileTypeActions.IFormTemplateFileTypeLoadActionCreator;
    filter: FormTemplateFileTypeActions.IFormTemplateFileTypeFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const FormTemplateFileTypesPage: FC<FormTemplateFileTypesPageProps> = ({
    formFilter,
    formTemplateFileTypes,
    loadingFormTemplateFileTypes,
    loadFormTemplateFileTypesSuccess,
    loadFormTemplateFileTypesFailure,
    loadFormTemplateFileTypesErrorCode,
    permissions,
    pageSize,
    clearFormTemplateFileTypes,
    loadFormTemplateFileTypes,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showFormTemplateFileTypes =
        !loadingFormTemplateFileTypes && loadFormTemplateFileTypesSuccess;


    // Load file type
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(FileTypesBreadcrumbs());

            loadFormTemplateFileTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        FileTypesBreadcrumbs,
        loadFormTemplateFileTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadFormTemplateFileTypesFailure) {
            navigate(`/error/${loadFormTemplateFileTypesErrorCode}`);
        }
    }, [
        loadFormTemplateFileTypesFailure,
        loadFormTemplateFileTypesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the file types on component unmount
    useEffect(() => {
        return () => {
            clearFormTemplateFileTypes();
        };
    }, [clearFormTemplateFileTypes]);

    const createTable = useMemo(() => {
        if (!formTemplateFileTypes || formTemplateFileTypes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no file types to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = formTemplateFileTypes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={formTemplateFileTypes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : formTemplateFileTypes.length
                    }
                    pageSize={
                        showPagination ? pageSize : formTemplateFileTypes.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no file types to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/settings/qa/formtemplatefiletypes/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        formTemplateFileTypes,
        PAGE_SIZE_OPTIONS,
        pageSize,
        DEFAULT_PAGE_SIZE,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>File Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.FormTemplateFileTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/qa/formtemplatefiletypes/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showFormTemplateFileTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showFormTemplateFileTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showFormTemplateFileTypes,
        createTable,
    ]);

    return (
        <QALookupsLayout
            loading={loadingFormTemplateFileTypes}
            permission={Dtos.Permission.FormTemplateFileTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<FormTemplateFileTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.formTemplateFileTypes.formFilter,
        formTemplateFileTypes: state.formTemplateFileTypes.formFilteredData,
        loadingFormTemplateFileTypes:
            state.formTemplateFileTypes.loadState &&
            state.formTemplateFileTypes.loadState.status ==
                RequestState.Pending,
        loadFormTemplateFileTypesSuccess:
            state.formTemplateFileTypes.loadState &&
            state.formTemplateFileTypes.loadState.status ==
                RequestState.Success,
        loadFormTemplateFileTypesFailure:
            state.formTemplateFileTypes.loadState &&
            state.formTemplateFileTypes.loadState.status ==
                RequestState.Failure,
        loadFormTemplateFileTypesErrorCode:
            state.formTemplateFileTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<FormTemplateFileTypesPageProps> => {
    return {
        loadFormTemplateFileTypes: bindActionCreators(
            FormTemplateFileTypeActions.LoadFormTemplateFileType,
            dispatch
        ),
        clearFormTemplateFileTypes: bindActionCreators(
            FormTemplateFileTypeActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            FormTemplateFileTypeActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FormTemplateFileTypesPage);
